import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { adminService } from '../../lib/api';

const EntreprisesTabAdvanced = ({ entreprises, fetchData }) => {
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showParametresModal, setShowParametresModal] = useState(false);
  const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showApprovisionModal, setShowApprovisionModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [approvisionnement, setApprovisionnement] = useState({
    montant: '',
    modePaiement: 'VIREMENT',
    reference: '',
    notes: ''
  });
  const [suspendReason, setSuspendReason] = useState('');
  const [parametres, setParametres] = useState({
    taux_commission: 5,
    plafond_avance: 0,
    ajustement_solde: 0
  });
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fonction utilitaire pour formater les devises
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF'
    }).format(amount || 0);
  };

  // Fonction utilitaire pour formater les dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Actions sur les entreprises
  const handleViewDetails = (entreprise) => {
    setSelectedEntreprise(entreprise);
    setShowDetailsModal(true);
  };

  const handleSuspendEntreprise = (entreprise) => {
    setSelectedEntreprise(entreprise);
    setSuspendReason('');
    setShowSuspendModal(true);
  };

  const handleReactivateEntreprise = (entreprise) => {
    setSelectedEntreprise(entreprise);
    setShowReactivateModal(true);
  };

  const handleConfigParametres = (entreprise) => {
    setSelectedEntreprise(entreprise);
    setParametres({
      taux_commission: entreprise.taux_commission || 5,
      plafond_avance: entreprise.plafond_avance || 0,
      ajustement_solde: 0
    });
    setShowParametresModal(true);
  };

  const handleViewHistorique = async (entreprise) => {
    setSelectedEntreprise(entreprise);
    setShowHistoriqueModal(true);
    setLoading(true);
    
    try {
      const data = await adminService.getHistoriqueEntreprise(entreprise.id);
      setHistorique(data.historique || []);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      setHistorique([]);
    } finally {
      setLoading(false);
    }
  };

  // Confirmer les actions
  const confirmSuspension = async () => {
    if (!suspendReason.trim()) {
      alert('Veuillez saisir une raison de suspension');
      return;
    }

    try {
      await adminService.suspendreEntreprise(selectedEntreprise.id, suspendReason);
      alert(`Entreprise "${selectedEntreprise.raison_sociale}" suspendue avec succès`);
      setShowSuspendModal(false);
      setSelectedEntreprise(null);
      setSuspendReason('');
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la suspension:', error);
      alert('Erreur lors de la suspension de l\'entreprise');
    }
  };

  const confirmReactivation = async () => {
    try {
      await adminService.reactiverEntreprise(selectedEntreprise.id);
      alert(`Entreprise "${selectedEntreprise.raison_sociale}" réactivée avec succès`);
      setShowReactivateModal(false);
      setSelectedEntreprise(null);
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la réactivation:', error);
      alert('Erreur lors de la réactivation de l\'entreprise');
    }
  };

  const confirmParametresChange = async () => {
    try {
      await adminService.updateParametresEntreprise(selectedEntreprise.id, parametres);
      alert(`Paramètres mis à jour pour "${selectedEntreprise.raison_sociale}"`);
      setShowParametresModal(false);
      setSelectedEntreprise(null);
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      alert('Erreur lors de la modification des paramètres');
    }
  };

  const handleResetPassword = (entreprise) => {
    setSelectedEntreprise(entreprise);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = async () => {
    try {
      const result = await adminService.resetEntreprisePassword(selectedEntreprise.id);
      setNewPassword(result.nouveau_password);
      alert(`✅ Mot de passe réinitialisé pour "${selectedEntreprise.raison_sociale}"!\n\nNouveau mot de passe: ${result.nouveau_password}\n\n⚠️ Transmettez ce mot de passe à l'entreprise de manière sécurisée.`);
      // Le modal reste ouvert pour afficher le nouveau mot de passe
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      alert('Erreur lors de la réinitialisation du mot de passe');
    }
  };

  const handleApprovisionner = (entreprise) => {
    setSelectedEntreprise(entreprise);
    setApprovisionnement({
      montant: '',
      modePaiement: 'VIREMENT',
      reference: '',
      notes: ''
    });
    setShowApprovisionModal(true);
  };

  const confirmApprovisionner = async () => {
    try {
      const montant = parseFloat(approvisionnement.montant);
      if (isNaN(montant) || montant === 0) {
        alert('Veuillez saisir un montant valide');
        return;
      }

      const result = await adminService.approvisionnerEntreprise(
        selectedEntreprise.id,
        montant,
        approvisionnement.modePaiement,
        approvisionnement.reference,
        approvisionnement.notes
      );

      alert(`✅ ${result.message}\n\nSolde précédent: ${result.solde_precedent.toLocaleString()} FG\nNouveau solde: ${result.nouveau_solde.toLocaleString()} FG`);
      setShowApprovisionModal(false);
      setSelectedEntreprise(null);
      fetchData();
    } catch (error) {
      console.error('Erreur lors de l\'approvisionnement:', error);
      alert('Erreur lors de l\'approvisionnement du compte');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Gestion Avancée des Entreprises ({entreprises.length})</CardTitle>
          <div className="flex space-x-2">
            <Button size="sm" variant="outline">📊 Export</Button>
            <Button size="sm">➕ Nouvelle entreprise</Button>
          </div>
        </CardHeader>
        <CardContent>
          {entreprises.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune entreprise enregistrée.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Entreprise</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Statut</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Employés</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Solde</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Commission</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Dernière activité</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions Admin</th>
                  </tr>
                </thead>
                <tbody>
                  {entreprises.map((entreprise) => (
                    <tr key={entreprise.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-gray-900">{entreprise.raison_sociale}</div>
                          <div className="text-sm text-gray-500">
                            NIF: {entreprise.nif} • {entreprise.mode_facturation}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          entreprise.statut === 'ACTIVE' 
                            ? 'bg-green-100 text-green-800'
                            : entreprise.statut === 'EN_ATTENTE_PAIEMENT'
                            ? 'bg-yellow-100 text-yellow-800'
                            : entreprise.statut === 'EN_REVUE_KYB'
                            ? 'bg-blue-100 text-blue-800'
                            : entreprise.statut === 'SUSPENDUE'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {entreprise.statut.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium">{entreprise.nb_employes || 0}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${entreprise.solde >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(entreprise.solde)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-blue-600">
                          {entreprise.taux_commission || 5}%
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-500">
                          {formatDate(entreprise.updated_at || entreprise.created_at)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewDetails(entreprise)}
                            title="Voir détails"
                          >
                            👁️
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleConfigParametres(entreprise)}
                            title="Configurer paramètres"
                          >
                            ⚙️
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewHistorique(entreprise)}
                            title="Voir historique"
                          >
                            📊
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-orange-300 text-orange-600 hover:bg-orange-50"
                            onClick={() => handleResetPassword(entreprise)}
                            title="Réinitialiser mot de passe"
                          >
                            🔑
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-green-300 text-green-600 hover:bg-green-50"
                            onClick={() => handleApprovisionner(entreprise)}
                            title="Approvisionner le compte"
                          >
                            💰
                          </Button>
                          {entreprise.statut === 'ACTIVE' ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              onClick={() => handleSuspendEntreprise(entreprise)}
                              title="Suspendre entreprise"
                            >
                              ⏸️
                            </Button>
                          ) : entreprise.statut === 'SUSPENDUE' ? (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleReactivateEntreprise(entreprise)}
                              title="Réactiver entreprise"
                            >
                              ▶️
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Détails */}
      {showDetailsModal && selectedEntreprise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Détails - {selectedEntreprise.raison_sociale}
              </h3>
              <button 
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Informations générales</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Raison sociale:</span>
                    <span className="font-medium">{selectedEntreprise.raison_sociale}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">NIF:</span>
                    <span className="font-medium">{selectedEntreprise.nif}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">RCCM:</span>
                    <span className="font-medium">{selectedEntreprise.rccm}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mode:</span>
                    <span className="font-medium">{selectedEntreprise.mode_facturation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contact:</span>
                    <div className="text-right">
                      <div className="font-medium">{selectedEntreprise.contact_nom}</div>
                      <div className="text-xs">{selectedEntreprise.contact_email}</div>
                      <div className="text-xs">{selectedEntreprise.contact_telephone}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Informations financières</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Solde actuel:</span>
                    <span className={`font-bold ${selectedEntreprise.solde >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(selectedEntreprise.solde)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Commission ALO:</span>
                    <span className="font-medium">{selectedEntreprise.taux_commission || 5}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Employés:</span>
                    <span className="font-medium">{selectedEntreprise.nb_employes || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date création:</span>
                    <span className="font-medium">{formatDate(selectedEntreprise.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Statut:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedEntreprise.statut === 'ACTIVE' 
                        ? 'bg-green-100 text-green-800'
                        : selectedEntreprise.statut === 'SUSPENDUE'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedEntreprise.statut.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button 
                variant="outline"
                onClick={() => setShowDetailsModal(false)}
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Suspension */}
      {showSuspendModal && selectedEntreprise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4 text-red-600">
              ⚠️ Suspendre l'entreprise "{selectedEntreprise.raison_sociale}"
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Cette action suspendra l'accès de l'entreprise et de ses employés à la plateforme.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raison de la suspension *
              </label>
              <textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Expliquez la raison de la suspension..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows="3"
              />
            </div>
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowSuspendModal(false);
                  setSelectedEntreprise(null);
                  setSuspendReason('');
                }}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={confirmSuspension}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={!suspendReason.trim()}
              >
                Confirmer la suspension
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Réactivation */}
      {showReactivateModal && selectedEntreprise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4 text-green-600">
              ✅ Réactiver l'entreprise "{selectedEntreprise.raison_sociale}"
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Cette action réactivera l'accès de l'entreprise et de ses employés à la plateforme.
            </p>
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowReactivateModal(false);
                  setSelectedEntreprise(null);
                }}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={confirmReactivation}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                Confirmer la réactivation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Paramètres */}
      {showParametresModal && selectedEntreprise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-medium mb-4">
              ⚙️ Paramètres - {selectedEntreprise.raison_sociale}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taux de commission ALO (%)
                </label>
                <Input
                  type="number"
                  value={parametres.taux_commission}
                  onChange={(e) => setParametres({...parametres, taux_commission: parseFloat(e.target.value)})}
                  min="0"
                  max="20"
                  step="0.1"
                />
                <p className="text-xs text-gray-500 mt-1">Entre 0% et 20%</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plafond d'avance (FG)
                </label>
                <Input
                  type="number"
                  value={parametres.plafond_avance}
                  onChange={(e) => setParametres({...parametres, plafond_avance: parseInt(e.target.value)})}
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Plafond maximum pour les avances</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ajustement de solde (FG)
                </label>
                <Input
                  type="number"
                  value={parametres.ajustement_solde}
                  onChange={(e) => setParametres({...parametres, ajustement_solde: parseInt(e.target.value)})}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Positive pour créditer, négative pour débiter. Solde actuel: {formatCurrency(selectedEntreprise.solde)}
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowParametresModal(false);
                  setSelectedEntreprise(null);
                }}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={confirmParametresChange}
                className="flex-1"
              >
                Sauvegarder
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique */}
      {showHistoriqueModal && selectedEntreprise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium">
                📊 Historique - {selectedEntreprise.raison_sociale}
              </h3>
              <button 
                onClick={() => setShowHistoriqueModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement de l'historique...</p>
              </div>
            ) : historique.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Aucun historique disponible</p>
              </div>
            ) : (
              <div className="space-y-3">
                {historique.map((event, index) => (
                  <div key={index} className="border-l-4 border-blue-200 pl-4 py-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium mb-1 ${
                          event.action === 'CREATION' ? 'bg-blue-100 text-blue-800' :
                          event.action === 'SUSPENSION' ? 'bg-red-100 text-red-800' :
                          event.action === 'REACTIVATION' ? 'bg-green-100 text-green-800' :
                          event.action === 'AJUSTEMENT_SOLDE' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {event.action}
                        </span>
                        <p className="text-sm font-medium">{event.description}</p>
                        <p className="text-xs text-gray-500">Par: {event.administrateur}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDate(event.date)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <Button 
                variant="outline"
                onClick={() => setShowHistoriqueModal(false)}
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Réinitialisation Mot de Passe */}
      {showResetPasswordModal && selectedEntreprise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">
              🔑 Réinitialiser le Mot de Passe
            </h3>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                Voulez-vous réinitialiser le mot de passe de l'entreprise :
              </p>
              <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                <p className="font-medium text-orange-900">{selectedEntreprise.raison_sociale}</p>
                <p className="text-sm text-orange-700">{selectedEntreprise.email}</p>
              </div>
              
              {!newPassword ? (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Un nouveau mot de passe aléatoire sera généré automatiquement.
                  </p>
                </div>
              ) : (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 font-medium mb-2">
                    ✅ Nouveau mot de passe généré :
                  </p>
                  <div className="bg-white p-3 rounded border border-green-300">
                    <p className="text-2xl font-mono font-bold text-green-900 text-center tracking-wider">
                      {newPassword}
                    </p>
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    ⚠️ Copiez ce mot de passe et transmettez-le à l'entreprise de manière sécurisée.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setSelectedEntreprise(null);
                  setNewPassword('');
                }}
                className="flex-1"
              >
                {newPassword ? 'Fermer' : 'Annuler'}
              </Button>
              {!newPassword && (
                <Button 
                  onClick={confirmResetPassword}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  🔑 Réinitialiser
                </Button>
              )}
              {newPassword && (
                <Button 
                  onClick={() => {
                    navigator.clipboard.writeText(newPassword);
                    alert('✅ Mot de passe copié dans le presse-papier !');
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  📋 Copier le Mot de Passe
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Approvisionnement */}
      {showApprovisionModal && selectedEntreprise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-medium mb-4">
              💰 Approvisionner le Compte
            </h3>
            
            <div className="mb-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
                <p className="font-medium text-blue-900">{selectedEntreprise.raison_sociale}</p>
                <p className="text-sm text-blue-700 mt-1">
                  Solde actuel: <span className="font-bold">{selectedEntreprise.solde?.toLocaleString() || 0} FG</span>
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant (FG) *
                  </label>
                  <input
                    type="number"
                    value={approvisionnement.montant}
                    onChange={(e) => setApprovisionnement({...approvisionnement, montant: e.target.value})}
                    placeholder="Ex: 10000000 (sans séparateur)"
                    min="0"
                    step="1000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Montant positif pour créditer, négatif pour débiter
                  </p>
                  {approvisionnement.montant && !isNaN(parseFloat(approvisionnement.montant)) && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-sm text-blue-900">
                        <strong>Montant saisi :</strong> {parseFloat(approvisionnement.montant).toLocaleString('fr-GN')} FG
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        <strong>Nouveau solde après opération :</strong> {(selectedEntreprise.solde + parseFloat(approvisionnement.montant)).toLocaleString('fr-GN')} FG
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mode de Paiement *
                  </label>
                  <select
                    value={approvisionnement.modePaiement}
                    onChange={(e) => setApprovisionnement({...approvisionnement, modePaiement: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="VIREMENT">Virement Bancaire</option>
                    <option value="MOBILE_MONEY">Mobile Money</option>
                    <option value="ESPECES">Espèces</option>
                    <option value="CHEQUE">Chèque</option>
                    <option value="AJUSTEMENT">Ajustement Manuel</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Référence Transaction
                  </label>
                  <input
                    type="text"
                    value={approvisionnement.reference}
                    onChange={(e) => setApprovisionnement({...approvisionnement, reference: e.target.value})}
                    placeholder="Ex: REF-20250121-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes / Commentaires
                  </label>
                  <textarea
                    value={approvisionnement.notes}
                    onChange={(e) => setApprovisionnement({...approvisionnement, notes: e.target.value})}
                    placeholder="Notes internes sur cette transaction..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ Cette opération modifiera immédiatement le solde de l'entreprise et sera enregistrée dans l'historique des transactions.
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowApprovisionModal(false);
                  setSelectedEntreprise(null);
                }}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={confirmApprovisionner}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                ✅ Confirmer l'Approvisionnement
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntreprisesTabAdvanced;